import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Monitor } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../stores/authStore'
import { useNavPanelStore, DEFAULT_SCREEN_STATE } from '../stores/navPanelStore'
import { usePanelPopout } from '../hooks/usePanelPopout'
import api from '../services/api'
import Sidebar from './Sidebar'
import Topbar from './Topbar'
import BackBar from './BackBar'
import DNAIntegrityBanner from './DNAIntegrityBanner'
import OrionAIAssistant from './OrionAIAssistant'
import { useAIStatus } from '../hooks/useAIStatus'
import OnderBalk from './OnderBalk'
import { ONDERBALK_HOOGTE_PX, CONTENT_HOOGTE_CALC } from '../theme/onderbalk'
import { OrionGuardian } from './OrionGuardian'
import TermsOfServiceModal from './TermsOfServiceModal'
import ContextPanel from './ContextPanel'

function Layout() {
  const { user, syncUserFromMe } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const { i18n } = useTranslation()
  const [showAIAssistant, setShowAIAssistant] = useState(false)
  const languageLoadStartedRef = useRef(false)

  // --- Drie-lagen navigatie (Sprint A) ---
  const schermtype = location.pathname.split('/').filter(Boolean)[0] || 'dashboard'
  const screens = useNavPanelStore((s) => s.screens)
  const setSplitRatio = useNavPanelStore((s) => s.setSplitRatio)
  const highlightMaterialId = useNavPanelStore((s) => s.highlightMaterialId)
  const { mode, panelOpen, splitRatio } = { ...DEFAULT_SCREEN_STATE, ...(screens[schermtype] || {}) }
  const panelActief = panelOpen && mode >= 2
  const splitscreen = panelActief && mode === 3

  // Paneel naar een tweede scherm (Document PiP / apart venster).
  const { popoutWin, isPopped, openPopout, closePopout } = usePanelPopout()
  // Haal het paneel terug zodra het in-app niet meer actief is (bv. mode 1).
  useEffect(() => {
    if (!panelActief && isPopped) closePopout()
  }, [panelActief, isPopped, closePopout])
  // Bij pop-out staat het paneel op een 2e scherm → hoofdscherm krijgt volledige breedte
  // (geen in-app paneelkolom; enkel een zwevend "Terughalen"-chipje).
  const splitActief = splitscreen && !isPopped

  // Drag-and-drop: een materiaal uit het paneel naar de werkruimte slepen voegt het toe aan
  // de offerte (via dezelfde handler als "Gebruik in offerte"). Alleen actief als een scherm
  // zo'n handler registreerde (bv. de offertefiche).
  const draggingMateriaal = useNavPanelStore((s) => s.draggingMateriaal)
  const useMateriaalHandler = useNavPanelStore((s) => s.useMateriaalHandler)
  const setDraggingMateriaal = useNavPanelStore((s) => s.setDraggingMateriaal)
  const dropActief = !!draggingMateriaal && !!useMateriaalHandler

  // Versleepbare deler (mode 3): live-ratio tijdens slepen, pas opslaan bij loslaten.
  const workspaceRef = useRef(null)
  const liveRatioRef = useRef(null)
  const [liveRatio, setLiveRatio] = useState(null)
  const effRatio = liveRatio ?? splitRatio
  const startDrag = (e) => {
    e.preventDefault()
    const onMove = (ev) => {
      const rect = workspaceRef.current?.getBoundingClientRect()
      if (!rect || rect.width === 0) return
      const r = Math.max(0.25, Math.min(0.85, (ev.clientX - rect.left) / rect.width))
      liveRatioRef.current = r
      setLiveRatio(r)
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
      if (liveRatioRef.current != null) setSplitRatio(schermtype, liveRatioRef.current)
      liveRatioRef.current = null
      setLiveRatio(null)
    }
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  // AI-assistent bestaat alleen als /ai/status ai_enabled geeft (#657, LLM-flag).
  const { aiEnabled } = useAIStatus()

  // Luister naar orion-ai-open event (bv. vanuit BlockedActionDialog)
  useEffect(() => {
    if (!aiEnabled) return undefined
    const handler = () => setShowAIAssistant(true)
    window.addEventListener('orion-ai-open', handler)
    return () => window.removeEventListener('orion-ai-open', handler)
  }, [aiEnabled])

  // Sync admin status van /auth/me bij elke mount (zorgt dat admin altijd alle tabs ziet)
  useEffect(() => {
    if (user?.id) syncUserFromMe?.()
  }, [user?.id])

  useEffect(() => {
    if (!user?.id || languageLoadStartedRef.current) return
    languageLoadStartedRef.current = true

    const loadUserLanguagePreference = async () => {
      const res = await api.get('/i18n/preferences/language', { timeout: 10000 }).catch((err) => {
        if (import.meta.env.DEV && err?.code !== 'ECONNABORTED') {
          console.warn('[Layout] i18n/preferences:', err?.message || err)
        } else if (import.meta.env.DEV && err?.code === 'ECONNABORTED') {
          console.warn('[Layout] i18n/preferences: timeout – controleer of de backend op port 8001 draait')
        }
        return null
      })
      if (res?.data?.language_code && res.data.language_code !== i18n.language) {
        await i18n.changeLanguage(res.data.language_code)
      }
    }

    loadUserLanguagePreference()
  }, [user?.id, i18n])
  
  return (
    <div className="min-h-screen bg-canvas dark:bg-gray-900">
      {/* Sidebar */}
      <Sidebar />
      
      {/* AI Assistant */}
      {aiEnabled && <OrionAIAssistant
        isOpen={showAIAssistant}
        onClose={() => setShowAIAssistant(false)}
        onActionCompleted={(result) => {
          // Handle navigation based on created objects
          if (result.created_objects) {
            if (result.created_objects.offerte_id) {
              navigate(`/offertes/${result.created_objects.offerte_id}`)
            } else if (result.created_objects.project_id) {
              navigate(`/projecten/${result.created_objects.project_id}/serviceoverzicht`)
            }
          }
        }}
      />}

      {/* Onboarding Tour - Uitgeschakeld */}
      {/* <OnboardingTour user={user} /> */}
      <TermsOfServiceModal user={user} onAccept={() => {
        // Zet direct in store zodat ToS-modal verdwijnt (ook als /users/me 422 geeft)
        useAuthStore.getState().setUserTosAccepted?.()
        if (user?.id) {
          api.get('/users/me').then(response => {
            // Samenvoegen, niet vervangen: /users/me kent geen tenant_id, is_admin,
            // is_superuser of is_setup_completed; die vielen hier stil weg.
            useAuthStore.setState((state) => ({ user: { ...state.user, ...response.data } }))
          }).catch((err) => {
            if (import.meta.env.DEV) console.warn('[Layout] users/me na ToS:', err?.message || err)
          })
        }
      }} />
      
      {/* Proactieve Guardian (alerts rechtsboven) — binnen OrionAIProvider-scope (App) */}
      <OrionGuardian />

      {/* Main content. Outlet blijft ALTIJD in dezelfde flex-wrapper, zodat de pagina
          niet her-mount bij het wisselen van navigatie-mode. */}
      <div className="pl-64">
        <Topbar schermtype={schermtype} />
        <div
          ref={workspaceRef}
          className={`flex ${splitActief ? 'overflow-hidden' : 'items-start'}`}
          style={splitActief ? { height: CONTENT_HOOGTE_CALC } : undefined}
        >
          <main
            className={`relative min-w-0 flex-1 p-8 dark:bg-gray-900 ${splitActief ? 'overflow-y-auto' : 'min-h-[calc(100vh-4rem)]'} ${dropActief ? 'ring-2 ring-inset ring-accent/70 bg-accent/5' : ''}`}
            style={splitActief ? { flex: `${effRatio} 1 0%` } : { paddingBottom: ONDERBALK_HOOGTE_PX }}
            onDragOver={(e) => {
              if (!useMateriaalHandler || !e.dataTransfer.types.includes('application/x-orion-materiaal')) return
              e.preventDefault()
              e.dataTransfer.dropEffect = 'copy'
            }}
            onDrop={(e) => {
              if (!useMateriaalHandler) return
              const raw = e.dataTransfer.getData('application/x-orion-materiaal')
              if (!raw && !draggingMateriaal) return
              e.preventDefault()
              let mat = draggingMateriaal
              if (raw) { try { mat = JSON.parse(raw) } catch { /* val terug op de store-waarde */ } }
              setDraggingMateriaal(null)
              if (mat) useMateriaalHandler(mat)
            }}
          >
            <BackBar />
            <DNAIntegrityBanner />
            <Outlet />
          </main>
          {panelActief && !isPopped && (
            <>
              {splitscreen && (
                <div
                  onMouseDown={startDrag}
                  className="w-1.5 shrink-0 cursor-col-resize bg-line dark:bg-gray-700 hover:bg-accent transition-colors"
                  title="Versleep om de verhouding aan te passen"
                />
              )}
              <aside
                className={splitscreen
                  ? 'min-w-0 overflow-y-auto'
                  : 'w-80 shrink-0 self-start sticky top-16 overflow-y-auto'}
                style={splitscreen ? { flex: `${1 - effRatio} 1 0%` } : { height: CONTENT_HOOGTE_CALC }}
              >
                <ContextPanel schermtype={schermtype} highlightMaterialId={highlightMaterialId} onPopout={openPopout} />
              </aside>
            </>
          )}
        </div>
      </div>

      {/* Sleephint tijdens drag-and-drop vanuit het paneel */}
      {dropActief && (
        <div className="pointer-events-none fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-control bg-accent text-white text-sm font-semibold shadow-card">
          Sleep op het offertescherm om toe te voegen
        </div>
      )}

      {/* Paneel op 2e scherm → hoofdscherm volledige breedte; zwevend chipje om terug te halen. */}
      {panelActief && isPopped && (
        <div className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-3 py-2 rounded-card border border-line dark:border-gray-700 bg-surface dark:bg-gray-800 shadow-card">
          <Monitor className="w-4 h-4 text-accent" />
          <span className="text-xs text-muted">Paneel op 2e scherm</span>
          <button type="button" onClick={closePopout} className="text-xs font-semibold px-2 py-1 rounded-control bg-accent/10 text-accent hover:bg-accent/20">
            Terughalen
          </button>
        </div>
      )}

      {/* Paneel op tweede scherm: zelfde JS-context (portal), dus "Gebruik in offerte" werkt direct. */}
      {popoutWin && createPortal(
        <div style={{ height: '100vh' }} className="bg-surface dark:bg-gray-900">
          <ContextPanel schermtype={schermtype} highlightMaterialId={highlightMaterialId} isPopped onReturn={closePopout} />
        </div>,
        popoutWin.document.body
      )}

      {/* Vaste onderbalk over de VOLLE vensterbreedte (Zoho-model): snelknoppen + tools + app-controls.
          Hoogte uit ONDERBALK_HOOGTE; sidebar-bodem en content-pb leiden uit dezelfde bron af. */}
      <OnderBalk onOpenAI={aiEnabled ? () => setShowAIAssistant(true) : undefined} />
    </div>
  )
}

export default Layout
